import { useEffect, useMemo, useState } from "react";
import { Button } from "antd";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

import { BANNER_SLIDES } from "../../utils/constants";
import { getFeaturedEventHeroImageUrl } from "../../utils/appUtils";
import EventImg from "../../assets/images/event-logo-fallback.png";
import { selectFeaturedEvents } from "../../redux/eventSlice";

export default function BannerSection() {
  const featuredEvents = useSelector(selectFeaturedEvents);

  const slides = useMemo(() => {
    if (!featuredEvents?.length) {
      return (BANNER_SLIDES || []).map((slide, index) => ({
        key: `placeholder-${index}`,
        src: slide.src,
        alt: slide.alt,
        eventSlug: null,
      }));
    }
    return featuredEvents.map((event) => ({
      key: `event-${event.id}`,
      src: getFeaturedEventHeroImageUrl(event) || EventImg,
      alt: event.event_name || "Event",
      eventSlug: event.slug,
    }));
  }, [featuredEvents]);

  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex(0);
  }, [slides.length]);

  useEffect(() => {
    if (slides.length <= 1) return undefined;

    const intervalId = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length);
    }, 4000);

    return () => window.clearInterval(intervalId);
  }, [slides.length]);

  return (
    <section className="hero">
      <div className="container heroSplitWrap">
        <div className="heroCopyCol">
          <span className="heroBadge">Discover New Experiences</span>
          <h1 className="heroTitle">
            Discover Events
            <span> That Inspire</span>
          </h1>
          <p className="heroDescription">
            Find concerts, workshops, meetups, and unforgettable experiences around you. Browse upcoming events
            and book your spot in minutes.
          </p>
          <div className="heroActions">
            <Link to="/discover-events">
              <Button type="primary" size="large" className="btnGradient">
                Browse Events
              </Button>
            </Link>
            <Link to="/host-event">
              <Button size="large" className="heroOutlineBtn">
                Sell Tickets
              </Button>
            </Link>
            <Link to="/invitation-builder">
              <Button size="large" className="heroOutlineBtn">
                Design an invitation
              </Button>
            </Link>
          </div>
          <div className="heroMetaList">
            <span className="heroMetaPill">Trusted Organizers</span>
            <span className="heroMetaPill">Secure Payments</span>
            <span className="heroMetaPill">Instant Booking</span>
          </div>
        </div>
        <div className="heroVisualCol">
          <div className="heroVisualSlider">
            {slides.map((slide, index) => {
              const inner = (
                <img src={slide.src} alt={slide.alt} loading={index === 0 ? "eager" : "lazy"} />
              );
              return (
                <article
                  className={`heroVisualSlide ${activeIndex === index ? "heroVisualSlideActive" : "heroVisualSlideInactive"}`}
                  key={slide.key}
                  aria-hidden={activeIndex !== index}
                >
                  {slide.eventSlug ? (
                    <Link to={`/event-details/${slide.eventSlug}`} className="heroVisualSlideLink">
                      {inner}
                    </Link>
                  ) : (
                    inner
                  )}
                </article>
              );
            })}
            {slides.length > 1 && (
              <div className="heroSliderMarkers" aria-label="Hero slide navigation">
                {slides.map((slide, index) => (
                  <button
                    key={`${slide.key}-marker`}
                    type="button"
                    className={`heroSliderMarker ${activeIndex === index ? "active" : ""}`}
                    onClick={() => setActiveIndex(index)}
                    aria-label={`Go to slide ${index + 1}`}
                    aria-pressed={activeIndex === index}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
